/**
 * IdentityMint.tsx
 * ─────────────────────────────────────────────────────────────
 * SBT (Soulbound Token) minting page for 10K Squad NFT holders.
 *
 * REQUIREMENTS:
 *  - User must connect Rabby wallet on Monad Testnet (chainId: 10143)
 *  - User must hold ≥1 NFT from the 10K Squad collection
 *    (0x818030837e8350ba63e64d7dc01a547fa73c8279)
 *  - Each wallet may mint exactly ONE SBT (enforced on-chain)
 *  - SBT name format: <handle>.10ksquad  (e.g. viper.10ksquad)
 *
 * WIRING:
 *  - Update SBT_CONTRACT_ADDRESS in src/lib/constants.ts once deployed
 *  - The ABI is in src/lib/sbt-abi.json
 * ─────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Sparkles, AlertTriangle, CheckCircle2, Loader2, ExternalLink, Copy, Info } from "lucide-react";
import { useMonadWallet } from "../hooks/useMonadWallet";
import { useSBTContract } from "../hooks/useSBTContract";
import WalletModal from "./WalletModal";

// ─── helpers ──────────────────────────────────────────────────

const HANDLE_REGEX = /^[a-z0-9_]{3,20}$/;

function validateHandle(h: string): string | null {
  if (!h) return "Name is required.";
  if (!HANDLE_REGEX.test(h))
    return "3–20 chars: lowercase letters, numbers, underscores only.";
  return null;
}

// ─── sub-components ───────────────────────────────────────────

function StepBadge({ n, active, done }: { n: number; active: boolean; done: boolean }) {
  return (
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono font-bold border-2 transition-all duration-300 ${
        done
          ? "bg-[#ff2680] border-[#ff2680] text-white"
          : active
          ? "border-[#ff2680] text-[#ff2680] bg-transparent"
          : "border-[#3d0033] text-[#3d0033] bg-transparent"
      }`}
    >
      {done ? <CheckCircle2 size={16} /> : n}
    </div>
  );
}

function Alert({
  type,
  children,
}: {
  type: "error" | "warn" | "success" | "info";
  children: React.ReactNode;
}) {
  const styles = {
    error:   "bg-red-950/40 border-red-600/40 text-red-300",
    warn:    "bg-yellow-950/40 border-yellow-600/40 text-yellow-300",
    success: "bg-emerald-950/40 border-emerald-500/40 text-emerald-300",
    info:    "bg-[#1a0016] border-[#3d0033] text-[#ff2680]/80",
  };
  const Icon = {
    error:   AlertTriangle,
    warn:    AlertTriangle,
    success: CheckCircle2,
    info:    Info,
  }[type];
  return (
    <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${styles[type]}`}>
      <Icon size={16} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

// ─── main page ────────────────────────────────────────────────

export default function IdentityMint() {
  // useMonadWallet kept exactly as original — address is string | null,
  // fully compatible with useSBTContract
  const {
    address,
    chainId,
    isConnected,
    isConnecting,
    connect,
    switchToMonad,
    disconnect,
    isWrongNetwork,
  } = useMonadWallet();

  const {
    holdsNFT,
    hasMintedSBT,
    mintedName,
    checkingEligibility,
    isMinting,
    txHash,
    mintError,
    checkAvailability,
    mint,
  } = useSBTContract(address);

  const [handle,        setHandle]        = useState("");
  const [handleError,   setHandleError]   = useState<string | null>(null);
  const [isAvailable,   setIsAvailable]   = useState<boolean | null>(null);
  const [checkingAvail, setCheckingAvail] = useState(false);
  const [copied,        setCopied]        = useState(false);

  useEffect(() => {
    setIsAvailable(null);
    const err = validateHandle(handle);
    setHandleError(err);
    if (err || !handle) return;

    const t = setTimeout(async () => {
      setCheckingAvail(true);
      const avail = await checkAvailability(handle);
      setIsAvailable(avail);
      setCheckingAvail(false);
    }, 600);
    return () => clearTimeout(t);
  }, [handle, checkAvailability]);

  const step =
    !isConnected ? 1 : isWrongNetwork ? 2 : checkingEligibility ? 3 : !holdsNFT ? 3 : hasMintedSBT ? 4 : 3;

  const canMint =
    isConnected &&
    !isWrongNetwork &&
    holdsNFT &&
    !hasMintedSBT &&
    !handleError &&
    isAvailable === true &&
    !isMinting;

  function copyAddress() {
    if (!address) return;
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  async function handleMint() {
    if (!canMint) return;
    await mint(handle);
  }

  return (
    <div className="min-h-screen bg-[#0a0008] bg-grid relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-radial-pink opacity-30" />

      <div className="relative max-w-2xl mx-auto px-4 py-16 sm:py-24">
        {/* ── header ── */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#3d0033] bg-[#1a0016] text-xs text-[#ff2680] font-mono mb-6">
            <ShieldCheck size={12} />
            HOLDERS ONLY · SOULBOUND · NON-TRANSFERABLE
          </div>

          <h1 className="font-display text-5xl sm:text-6xl text-white glow-text mb-3">
            CLAIM YOUR
            <span className="shimmer-text block">.10KSQUAD NAME</span>
          </h1>

          <p className="font-body text-sm text-[#ff2680]/60 max-w-md mx-auto leading-relaxed">
            Mint your soulbound identity token. One name per wallet, forever
            on-chain. Exclusive to 10K Squad NFT holders.
          </p>
        </motion.div>

        {/* ── steps ── */}
        <div className="flex items-center justify-center gap-3 mb-10">
          {["Connect", "Network", "Mint"].map((label, i) => (
            <div key={label} className="flex items-center gap-3">
              <div className="flex flex-col items-center gap-1">
                <StepBadge n={i + 1} active={step === i + 1} done={step > i + 1} />
                <span className="text-[10px] font-mono text-[#ff2680]/50 uppercase">{label}</span>
              </div>
              {i < 2 && <div className="w-10 h-px bg-[#3d0033] mb-4" />}
            </div>
          ))}
        </div>

        {/* ── main card ── */}
        <motion.div layout className="card-glass glow-border-strong rounded-2xl p-6 sm:p-8 space-y-6">
          <AnimatePresence mode="wait">

            {/* ── STEP 1: connect wallet ── */}
            {!isConnected && (
              <motion.div key="connect" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <Alert type="info">
                  Connect your wallet to verify your 10K Squad NFT ownership.
                  Rabby, MetaMask, Coinbase Wallet and more are supported.
                </Alert>
                {/* WalletModal: opens RainbowKit picker, then calls connect from useMonadWallet */}
                <WalletModal onConnect={connect} isConnecting={isConnecting} />
              </motion.div>
            )}

            {/* ── STEP 2: wrong network ── */}
            {isConnected && isWrongNetwork && (
              <motion.div key="network" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[#1a0016] border border-[#3d0033]">
                  <div className="w-2 h-2 rounded-full bg-[#ff2680] animate-pulse" />
                  <span className="font-mono text-xs text-white/60 truncate">{address}</span>
                  <button onClick={disconnect} className="ml-auto text-[10px] text-[#ff2680]/50 hover:text-[#ff2680] font-mono uppercase">
                    Disconnect
                  </button>
                </div>
                <Alert type="warn">
                  You're on the wrong network. Switch to <strong>Monad Mainnet</strong> (Chain ID: 143).
                </Alert>
                <button
                  onClick={switchToMonad}
                  className="btn-pink w-full flex items-center justify-center gap-2 py-3.5 text-sm font-mono"
                >
                  Switch to Monad Mainnet
                </button>
              </motion.div>
            )}

            {/* ── STEP 3: eligibility + mint ── */}
            {isConnected && !isWrongNetwork && (
              <motion.div key="mint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-5">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[#1a0016] border border-[#3d0033]">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="font-mono text-xs text-white/70 truncate flex-1">{address}</span>
                  <button onClick={copyAddress} className="text-[#ff2680]/50 hover:text-[#ff2680] transition-colors" title="Copy address">
                    {copied ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                  </button>
                  <button onClick={disconnect} className="text-[10px] text-[#ff2680]/50 hover:text-[#ff2680] font-mono uppercase">
                    Disconnect
                  </button>
                </div>

                {checkingEligibility && (
                  <div className="flex items-center gap-2 text-sm text-[#ff2680]/60 font-mono">
                    <Loader2 size={14} className="animate-spin" />
                    Verifying NFT ownership…
                  </div>
                )}

                {!checkingEligibility && !holdsNFT && (
                  <Alert type="error">
                    This wallet doesn't hold any 10K Squad NFTs. Only holders may mint a .10ksquad identity.{" "}
                    <a href="https://opensea.io/collection/the-10k-squad-350905768" target="_blank" rel="noreferrer" className="underline">
                      Get one on OpenSea ↗
                    </a>
                  </Alert>
                )}

                {!checkingEligibility && holdsNFT && hasMintedSBT && (
                  <div className="space-y-3">
                    <Alert type="success">You already own a .10ksquad identity!</Alert>
                    <div className="p-4 rounded-xl bg-[#1a0016] border border-[#3d0033] text-center">
                      <p className="font-mono text-2xl text-white glow-text">
                        {mintedName}<span className="text-[#ff2680]">.10ksquad</span>
                      </p>
                      <p className="text-xs text-white/40 mt-1 font-mono">Soulbound — non-transferable</p>
                    </div>
                  </div>
                )}

                {!checkingEligibility && holdsNFT && !hasMintedSBT && (
                  <div className="space-y-5">
                    <Alert type="info">
                      You hold a 10K Squad NFT ✓ — choose your permanent on-chain name below.
                    </Alert>

                    <div className="space-y-2">
                      <label className="block text-xs font-mono text-[#ff2680]/60 uppercase tracking-widest">
                        Your Handle
                      </label>
                      <div className="relative flex items-center">
                        <input
                          type="text"
                          value={handle}
                          onChange={(e) => setHandle(e.target.value.toLowerCase())}
                          placeholder="yourname"
                          maxLength={20}
                          className={`w-full bg-[#0a0008] border rounded-xl px-4 py-3 pr-28 font-mono text-sm text-white placeholder-white/20 outline-none transition-all duration-200 focus:ring-2 ${
                            handleError
                              ? "border-red-600/60 focus:ring-red-600/20"
                              : isAvailable === true
                              ? "border-emerald-500/60 focus:ring-emerald-500/20"
                              : "border-[#3d0033] focus:ring-[#ff2680]/20"
                          }`}
                        />
                        <span className="absolute right-4 font-mono text-xs text-[#ff2680]/60 pointer-events-none select-none">
                          .10ksquad
                        </span>
                      </div>

                      <AnimatePresence mode="wait">
                        {handleError && handle && (
                          <motion.p key="err" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-xs text-red-400 font-mono">
                            {handleError}
                          </motion.p>
                        )}
                        {checkingAvail && !handleError && (
                          <motion.p key="checking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-xs text-[#ff2680]/50 font-mono flex items-center gap-1">
                            <Loader2 size={10} className="animate-spin" /> Checking availability…
                          </motion.p>
                        )}
                        {isAvailable === true && !handleError && (
                          <motion.p key="avail" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-xs text-emerald-400 font-mono flex items-center gap-1">
                            <CheckCircle2 size={11} /> {handle}.10ksquad is available!
                          </motion.p>
                        )}
                        {isAvailable === false && !handleError && (
                          <motion.p key="taken" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-xs text-red-400 font-mono">
                            Already taken. Try a different name.
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {handle && !handleError && (
                      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="p-4 rounded-xl bg-[#0d0009] border border-[#3d0033] text-center">
                        <p className="text-xs font-mono text-white/30 uppercase tracking-widest mb-1">Preview</p>
                        <p className="font-mono text-2xl text-white">
                          {handle}<span className="text-[#ff2680]">.10ksquad</span>
                        </p>
                      </motion.div>
                    )}

                    {mintError && <Alert type="error">{mintError}</Alert>}

                    {txHash && !mintError && (
                      <Alert type="success">
                        Transaction submitted!{" "}
                        <a href={`https://monadexplorer.com/tx/${txHash}`} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">
                          View on explorer <ExternalLink size={11} />
                        </a>
                      </Alert>
                    )}

                    <button
                      onClick={handleMint}
                      disabled={!canMint}
                      className={`btn-pink w-full flex items-center justify-center gap-2 py-4 text-sm font-mono transition-all duration-200 ${
                        !canMint ? "opacity-40 cursor-not-allowed" : "hover:shadow-[0_0_30px_rgba(255,38,128,0.4)]"
                      }`}
                    >
                      {isMinting ? (
                        <><Loader2 size={16} className="animate-spin" /> Minting…</>
                      ) : (
                        <><Sparkles size={16} /> Mint {handle ? `${handle}.10ksquad` : ".10ksquad"}</>
                      )}
                    </button>

                    <p className="text-center text-[10px] text-white/20 font-mono">
                      Soulbound · Non-transferable · One per wallet · Permanent
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* ── info strip ── */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-8 grid grid-cols-3 gap-3">
          {[
            { label: "Supply", value: "3,333" },
            { label: "Chain",  value: "Monad"  },
            { label: "Type",   value: "SBT"    },
          ].map(({ label, value }) => (
            <div key={label} className="card-glass rounded-xl p-3 text-center border border-[#3d0033]">
              <p className="font-mono text-base text-white">{value}</p>
              <p className="text-[10px] text-white/30 uppercase font-mono mt-0.5">{label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}